<template>
  <div class="min-h-screen bg-zinc-950 p-8 text-zinc-100">
    <div class="max-w-6xl mx-auto space-y-8">
      
      <!-- Header -->
      <header class="flex justify-between items-center bg-zinc-900/50 backdrop-blur-md p-6 rounded-3xl shadow-sm border border-zinc-800/50 relative overflow-hidden">
        <div class="relative z-10 flex justify-between items-center w-full">
          <div>
            <router-link :to="{ name: 'music_analytics' }" class="inline-flex items-center gap-2 text-zinc-400 hover:text-emerald-400 transition-colors text-sm font-medium mb-4">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
              Retour aux statistiques
            </router-link>
            <h1 class="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-green-600">Mes Playlists Synchronisées</h1>
            <p class="text-zinc-400 mt-2">Gérez vos playlists générées par Codifyo</p>
          </div>
          
          <button @click="openCreateModal" class="bg-emerald-500 hover:bg-emerald-600 text-zinc-950 font-bold py-3 px-6 rounded-2xl shadow-lg shadow-emerald-500/20 transition-all flex items-center gap-2">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path></svg>
            Nouvelle Playlist Multi-Artistes
          </button>
        </div>
      </header>

      <div v-if="loading" class="text-center py-20">
        <div class="w-16 h-16 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin mx-auto mb-4"></div>
      </div>

      <!-- Liste des playlists -->
      <div v-else-if="playlists.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div v-for="playlist in playlists" :key="playlist.id" class="bg-zinc-900/40 backdrop-blur-sm rounded-3xl border border-zinc-800/50 p-6 flex flex-col justify-between hover:bg-zinc-800/50 transition-colors">
          <div>
            <div class="flex justify-between items-start mb-4">
              <div class="p-3 rounded-2xl" :class="playlist.type === 'genre' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'">
                <svg v-if="playlist.type === 'genre'" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"></path></svg>
                <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-xs font-mono text-zinc-500">{{ playlist.type === 'genre' ? 'Par Genre' : 'Artistes' }}</span>
                <!-- Edit button for mb_artists -->
                <button v-if="playlist.type === 'mb_artists'" @click="openEditModal(playlist)" class="text-zinc-500 hover:text-emerald-400 transition-colors p-1 rounded-lg hover:bg-zinc-700/50">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                </button>
              </div>
            </div>
            
            <h3 class="text-xl font-bold text-zinc-200 mb-1 truncate" :title="playlist.name">{{ playlist.name }}</h3>

            <!-- Artists summary for mb_artists -->
            <div v-if="playlist.type === 'mb_artists' && playlist.configuration?.artists?.length" class="text-xs text-zinc-500 mb-2 truncate">
              {{ playlist.configuration.artists.map((a: any) => a.name).join(', ') }}
            </div>
            <!-- Excluded keywords -->
            <div v-if="playlist.configuration?.excluded_keywords?.length" class="flex flex-wrap gap-1 mb-2">
              <span v-for="kw in playlist.configuration.excluded_keywords" :key="kw" class="bg-red-500/10 text-red-400 text-xs px-2 py-0.5 rounded-full font-mono">-{{ kw }}</span>
            </div>
            
            <p class="text-sm text-zinc-500 mb-6">
              Dernière synchro : {{ playlist.lastSyncedAt ? formatDate(playlist.lastSyncedAt) : 'En attente...' }}
            </p>

            <button 
              @click="askAiForSuggestions(playlist)"
              :disabled="aiLoadingId === playlist.id"
              class="w-full bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 font-bold py-2 px-4 rounded-xl text-sm flex items-center justify-center gap-2 transition-all mb-3 disabled:opacity-50"
            >
              <svg v-if="aiLoadingId !== playlist.id" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
              <div v-else class="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
              {{ aiLoadingId === playlist.id ? 'Génération...' : '✨ Suggestions IA' }}
            </button>
          </div>
          
          <a :href="`https://open.spotify.com/playlist/${playlist.spotifyPlaylistId}`" target="_blank" class="w-full bg-zinc-800 hover:bg-zinc-700 text-emerald-400 border border-emerald-500/30 font-bold py-3 px-4 rounded-xl text-sm flex items-center justify-center gap-2 transition-all">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 14.5c-2.49 0-4.5-2.01-4.5-4.5S9.51 7.5 12 7.5s4.5 2.01 4.5 4.5-2.01 4.5-4.5 4.5zm0-7c-1.38 0-2.5 1.12-2.5 2.5s1.12 2.5 2.5 2.5 2.5-1.12 2.5-2.5-1.12-2.5-2.5-2.5z"/></svg>
            Ouvrir dans Spotify
          </a>
        </div>
      </div>

      <div v-else class="text-center py-20 bg-zinc-900/40 rounded-3xl border border-zinc-800/50">
        <svg class="w-16 h-16 mx-auto text-zinc-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"></path></svg>
        <h2 class="text-xl font-bold text-zinc-300">Aucune playlist synchronisée</h2>
        <p class="text-zinc-500 mt-2">Générez une playlist depuis la page d'un genre ou créez-en une ici.</p>
      </div>

      <!-- Modal Création / Édition -->
      <div v-if="showModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/80 backdrop-blur-sm">
        <div class="bg-zinc-900 border border-zinc-800 rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
          <div class="p-6 border-b border-zinc-800 flex justify-between items-center">
            <h2 class="text-2xl font-bold text-zinc-100">{{ editingPlaylist ? 'Modifier la Playlist' : 'Créer une Playlist Multi-Artistes' }}</h2>
            <button @click="closeModal" class="text-zinc-400 hover:text-zinc-100"><svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg></button>
          </div>
          
          <div class="p-6 overflow-y-auto space-y-6 flex-1">

            <!-- Nom de la playlist -->
            <div>
              <label class="block text-sm font-medium text-zinc-400 mb-2">Nom de la playlist</label>
              <input type="text" v-model="newPlaylistName" placeholder="Ex: Mes découvertes Rock" class="w-full bg-zinc-800 border border-zinc-700 text-zinc-100 rounded-xl px-4 py-3 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500">
            </div>

            <!-- Recherche Artiste -->
            <div>
              <label class="block text-sm font-medium text-zinc-400 mb-2">Rechercher un artiste (MusicBrainz)</label>
              <div class="relative">
                <input type="text" v-model="searchQuery" @input="debounceSearch" placeholder="Rechercher..." class="w-full bg-zinc-800 border border-zinc-700 text-zinc-100 rounded-xl px-4 py-3 pl-10 focus:outline-none focus:border-emerald-500">
                <svg class="w-5 h-5 absolute left-3 top-3.5 text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
              </div>

              <div v-if="searchResults.length > 0" class="mt-2 bg-zinc-800 border border-zinc-700 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                <div v-for="res in searchResults" :key="res.mbid" @click="addArtist(res)" class="px-4 py-3 hover:bg-zinc-700 cursor-pointer border-b border-zinc-700/50 last:border-0 flex justify-between items-center">
                  <div>
                    <div class="font-bold text-zinc-200">{{ res.name }}</div>
                    <div v-if="res.disambiguation" class="text-xs text-zinc-500">{{ res.disambiguation }}</div>
                  </div>
                  <button class="text-emerald-500 hover:text-emerald-400">Ajouter</button>
                </div>
              </div>
              <div v-if="isSearching" class="mt-2 text-center text-sm text-zinc-500">Recherche en cours...</div>
            </div>

            <!-- Artistes sélectionnés -->
            <div>
              <label class="block text-sm font-medium text-zinc-400 mb-2">Artistes sélectionnés ({{ selectedArtists.length }})</label>
              <div v-if="selectedArtists.length === 0" class="text-sm text-zinc-600 italic">Aucun artiste sélectionné.</div>
              <div class="flex flex-wrap gap-2">
                <div v-for="artist in selectedArtists" :key="artist.mbid" class="bg-zinc-800 border border-zinc-700 px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm">
                  <span class="text-zinc-200 font-medium">{{ artist.name }}</span>
                  <button @click="removeArtist(artist.mbid)" class="text-zinc-500 hover:text-red-400"><svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg></button>
                </div>
              </div>
            </div>

            <!-- Mots exclus -->
            <div>
              <label class="block text-sm font-medium text-zinc-400 mb-2">Mots-clés exclus</label>
              <p class="text-xs text-zinc-500 mb-3">Tout titre contenant l'un de ces mots (ex: "demo", "live", "instrumental") sera exclu de la playlist.</p>
              <div class="flex gap-2 mb-3">
                <input
                  type="text"
                  v-model="newKeyword"
                  @keydown.enter.prevent="addKeyword"
                  placeholder="Ex: demo, live, instrumental..."
                  class="flex-1 bg-zinc-800 border border-zinc-700 text-zinc-100 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
                >
                <button @click="addKeyword" class="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors">
                  Ajouter
                </button>
              </div>
              <div v-if="excludedKeywords.length > 0" class="flex flex-wrap gap-2">
                <div v-for="kw in excludedKeywords" :key="kw" class="bg-red-500/10 border border-red-500/20 text-red-400 px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm font-mono">
                  <span>{{ kw }}</span>
                  <button @click="removeKeyword(kw)" class="text-red-500/50 hover:text-red-400"><svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg></button>
                </div>
              </div>
              <div v-else class="text-sm text-zinc-600 italic">Aucun mot exclu.</div>
            </div>

            <!-- Prévisualisation des titres -->
            <div class="border-t border-zinc-800 pt-6">
              <h3 class="text-sm font-semibold text-zinc-400 mb-2 flex items-center justify-between">
                <span>Prévisualisation des morceaux ({{ previewTracksList.filter(t => !t.excluded).length }}/{{ previewTracksList.length }})</span>
                <span v-if="loadingPreview" class="text-xs text-zinc-500 flex items-center gap-1">
                  <span class="w-3.5 h-3.5 border border-zinc-500 border-t-transparent rounded-full animate-spin"></span>
                  Chargement...
                </span>
              </h3>
              
              <div v-if="previewHasErrors" class="bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[11px] px-4 py-2.5 rounded-xl mb-3 flex items-center gap-2">
                <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                <span>Certains albums ou morceaux n'ont pas pu être récupérés en raison d'une limite de quota Spotify (Rate Limit). La liste ci-dessous est incomplète.</span>
              </div>

              <div v-if="previewTracksList.length > 0" class="bg-zinc-950/50 rounded-xl border border-zinc-800/80 max-h-60 overflow-y-auto divide-y divide-zinc-900">
                <div v-for="track in previewTracksList" :key="track.title" class="px-4 py-2 flex items-center justify-between text-xs" :class="track.excluded ? 'text-zinc-600 bg-zinc-950/20' : 'text-zinc-300'">
                  <div class="flex items-center gap-2 min-w-0">
                    <!-- Status Icon -->
                    <span v-if="track.excluded" class="text-red-400/70" title="Exclu de la playlist">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path></svg>
                    </span>
                    <span v-else class="text-emerald-400" title="Inclus dans la playlist">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>
                    </span>
                    <span class="truncate font-medium">{{ track.title }}</span>
                  </div>
                  
                  <!-- Exclusion Reason / Badges -->
                  <div class="flex items-center gap-2 shrink-0 text-[10px]">
                    <span v-if="track.reason === 'no_spotify'" class="bg-zinc-800 text-zinc-500 px-2 py-0.5 rounded font-mono">Pas de lien Spotify</span>
                    <span v-else-if="track.reason === 'keyword'" class="bg-red-950/30 text-red-400 px-2 py-0.5 rounded font-mono">Mot clé: "{{ track.matchedKeyword }}"</span>
                    <span v-else-if="track.reason === 'duplicate'" class="bg-yellow-950/30 text-yellow-400 px-2 py-0.5 rounded font-mono">Doublon</span>
                    <span v-else-if="!track.excluded" class="text-zinc-500 font-mono">{{ track.spotifyId }}</span>
                  </div>
                </div>
              </div>
              <div v-else-if="!loadingPreview" class="text-xs text-zinc-600 italic">
                Sélectionnez au moins un artiste pour prévisualiser les morceaux.
              </div>
            </div>
          </div>
          
          <div class="p-6 border-t border-zinc-800 bg-zinc-900/50 flex justify-end gap-3">
            <button @click="closeModal" class="px-6 py-2 rounded-xl text-zinc-400 hover:text-zinc-200 transition-colors">Annuler</button>
            <button @click="submitPlaylist" :disabled="!isFormValid || submitting" class="bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-500/30 disabled:cursor-not-allowed text-zinc-950 font-bold py-2 px-6 rounded-xl transition-all flex items-center gap-2">
              <span v-if="submitting" class="w-4 h-4 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></span>
              {{ editingPlaylist ? 'Enregistrer et resynchroniser' : 'Créer la playlist' }}
            </button>
          </div>
        </div>
      </div>

    </div>

    <!-- Modal AI Suggestions -->
    <div v-if="showAiModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/80 backdrop-blur-sm">
      <div class="bg-zinc-900 border border-blue-500/30 rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
        <div class="p-6 border-b border-zinc-800 flex justify-between items-center relative overflow-hidden">
          <div class="absolute -right-10 -top-10 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl"></div>
          <h2 class="text-2xl font-bold text-blue-400 relative z-10 flex items-center gap-2">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
            Suggestions pour "{{ activeAiPlaylist?.name }}"
          </h2>
          <button @click="showAiModal = false" class="text-zinc-400 hover:text-zinc-100 relative z-10"><svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg></button>
        </div>
        
        <div class="p-6 overflow-y-auto">
          <div class="prose prose-invert prose-blue max-w-none prose-p:leading-relaxed" v-html="formattedAiSuggestion"></div>
        </div>
        
        <div class="p-6 border-t border-zinc-800 bg-zinc-900/50 flex justify-end">
          <button @click="showAiModal = false" class="px-6 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 transition-colors">Fermer</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useToast } from '../../composables/useToast'

const { error: showError, success: showSuccess } = useToast()

const playlists = ref<any[]>([])
const loading = ref(true)

// Modal state (shared create/edit)
const showModal = ref(false)
const editingPlaylist = ref<any>(null)
const newPlaylistName = ref('')
const searchQuery = ref('')
const searchResults = ref<any[]>([])
const isSearching = ref(false)
const selectedArtists = ref<any[]>([])
const excludedKeywords = ref<string[]>([])
const newKeyword = ref('')
const submitting = ref(false)

// Preview tracks state
const previewTracksList = ref<any[]>([])
const previewHasErrors = ref(false)
const loadingPreview = ref(false)

// AI state
const aiLoadingId = ref<number|null>(null)
const showAiModal = ref(false)
const activeAiPlaylist = ref<any>(null)
const aiSuggestion = ref('')
const formattedAiSuggestion = computed(() => {
  if (!aiSuggestion.value) return ''
  let text = aiSuggestion.value
  text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
  text = text.replace(/\n- (.*?)(?=\n|$)/g, '<li class="ml-4 list-disc">$1</li>')
  text = text.replace(/\n/g, '<br>')
  return text
})

let searchTimeout: any = null
let previewTimeout: any = null

const fetchPreviewTracks = async () => {
  if (selectedArtists.value.length === 0) {
    previewTracksList.value = []
    previewHasErrors.value = false
    return
  }
  loadingPreview.value = true
  try {
    const token = localStorage.getItem('apiToken')
    const response = await fetch('/api/music/analytics/playlists/preview-tracks', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        mbIds: selectedArtists.value.map(a => a.mbid),
        excludedKeywords: excludedKeywords.value
      })
    })
    if (response.ok) {
      const data = await response.json()
      previewTracksList.value = data.tracks || []
      previewHasErrors.value = data.hasErrors || false
    }
  } catch (e) {
    console.error(e)
  } finally {
    loadingPreview.value = false
  }
}

// Watch selectedArtists and excludedKeywords to fetch preview in real-time
watch(
  [selectedArtists, excludedKeywords],
  () => {
    if (previewTimeout) clearTimeout(previewTimeout)
    previewTimeout = setTimeout(() => {
      fetchPreviewTracks()
    }, 300)
  },
  { deep: true }
)

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  })
}

const fetchPlaylists = async () => {
  loading.value = true
  try {
    const token = localStorage.getItem('apiToken')
    const response = await fetch('/api/music/analytics/playlists', {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    if (response.ok) {
      playlists.value = await response.json()
    }
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

const openCreateModal = () => {
  editingPlaylist.value = null
  newPlaylistName.value = ''
  selectedArtists.value = []
  excludedKeywords.value = []
  previewTracksList.value = []
  previewHasErrors.value = false
  searchQuery.value = ''
  searchResults.value = []
  showModal.value = true
}

const openEditModal = (playlist: any) => {
  editingPlaylist.value = playlist
  newPlaylistName.value = playlist.name || ''
  selectedArtists.value = [...(playlist.configuration?.artists ?? [])]
  excludedKeywords.value = [...(playlist.configuration?.excluded_keywords ?? [])]
  previewHasErrors.value = false
  searchQuery.value = ''
  searchResults.value = []
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  editingPlaylist.value = null
  previewTracksList.value = []
  previewHasErrors.value = false
}

const debounceSearch = () => {
  if (searchTimeout) clearTimeout(searchTimeout)
  if (searchQuery.value.trim().length < 2) {
    searchResults.value = []
    return
  }
  searchTimeout = setTimeout(async () => {
    isSearching.value = true
    try {
      const token = localStorage.getItem('apiToken')
      const response = await fetch(`/api/music/analytics/search-mb-artist?q=${encodeURIComponent(searchQuery.value)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        searchResults.value = await response.json()
      }
    } catch (e) {
      console.error(e)
    } finally {
      isSearching.value = false
    }
  }, 500)
}

const addArtist = (artist: any) => {
  if (!selectedArtists.value.find(a => a.mbid === artist.mbid)) {
    selectedArtists.value.push(artist)
  }
  searchQuery.value = ''
  searchResults.value = []
}

const removeArtist = (mbid: string) => {
  selectedArtists.value = selectedArtists.value.filter(a => a.mbid !== mbid)
}

const addKeyword = () => {
  const kw = newKeyword.value.trim().toLowerCase()
  if (kw && !excludedKeywords.value.includes(kw)) {
    excludedKeywords.value.push(kw)
  }
  newKeyword.value = ''
}

const removeKeyword = (kw: string) => {
  excludedKeywords.value = excludedKeywords.value.filter(k => k !== kw)
}

const isFormValid = computed(() => {
  return newPlaylistName.value.trim().length > 0 && selectedArtists.value.length > 0
})

const submitPlaylist = async () => {
  if (!isFormValid.value) return
  submitting.value = true

  try {
    const token = localStorage.getItem('apiToken')

    if (editingPlaylist.value) {
      // Edit existing
      const response = await fetch(`/api/music/analytics/playlists/${editingPlaylist.value.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newPlaylistName.value.trim(),
          mbIds: selectedArtists.value.map(a => a.mbid),
          artists: selectedArtists.value,
          excludedKeywords: excludedKeywords.value,
        })
      })
      if (!response.ok) throw new Error('Erreur modification')
      showSuccess("Playlist mise à jour ! La synchronisation va démarrer en arrière-plan.")
    } else {
      // Create new
      const response = await fetch('/api/music/analytics/playlists/custom-artists', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newPlaylistName.value.trim(),
          mbIds: selectedArtists.value.map(a => a.mbid),
          artists: selectedArtists.value,
          excludedKeywords: excludedKeywords.value,
        })
      })
      if (!response.ok) throw new Error('Erreur création')
      showSuccess("Playlist créée ! La synchronisation des titres va s'effectuer en arrière-plan.")
    }

    closeModal()
    await fetchPlaylists()
  } catch (e) {
    showError(editingPlaylist.value ? "Erreur lors de la modification" : "Erreur lors de la création")
  } finally {
    submitting.value = false
  }
}

const askAiForSuggestions = async (playlist: any) => {
  if (aiLoadingId.value) return
  aiLoadingId.value = playlist.id
  aiSuggestion.value = ''
  
  try {
    const token = localStorage.getItem('apiToken')
    
    // Contexte basé sur le type
    let sample: string[] = []
    if (playlist.type === 'mb_artists' && playlist.configuration?.artists) {
      sample = playlist.configuration.artists.map((a: any) => a.name)
    } else if (playlist.type === 'genre') {
      sample = [playlist.name] // Pour un genre, le nom suffit comme contexte de base
    }

    const response = await fetch(`/api/music/ai/suggest`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: 'playlist',
        name: playlist.name,
        sample: sample
      })
    })
    
    if (!response.ok) {
        throw new Error('Erreur lors de la récupération des suggestions.')
    }
    
    const data = await response.json()
    aiSuggestion.value = data.suggestion
    activeAiPlaylist.value = playlist
    showAiModal.value = true
  } catch (e: any) {
    console.error(e)
    showError(e.message || "Impossible d'obtenir des suggestions.")
  } finally {
    aiLoadingId.value = null
  }
}

onMounted(() => {
  fetchPlaylists()
})
</script>
